import { Analytics02Icon, Cancel01Icon, PlusSignIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { TooltipRenderProps } from 'react-joyride';

import { GmailIcon, MailIcon } from '../../components/pure/ai-island/icons';
import * as styles from './custom-tooltip.css';
import type { PiqueOnboardingStep } from './types';

const CreateNotesIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="none">
    <path
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="0.3"
      d="M15.732,1.835 L16.27,4.227 C16.664,5.973 18.027,7.336 19.773,7.73 L22.165,8.268 C22.507,8.345 22.75,8.649 22.75,9 C22.75,9.351 22.507,9.655 22.165,9.732 L19.773,10.27 C18.027,10.664 16.664,12.027 16.27,13.773 L15.732,16.165 C15.655,16.507 15.351,16.75 15,16.75 C14.649,16.75 14.345,16.507 14.268,16.165 L13.73,13.773 C13.336,12.027 11.973,10.664 10.227,10.27 L7.835,9.732 C7.493,9.655 7.25,9.351 7.25,9 C7.25,8.649 7.493,8.345 7.835,8.268 L10.227,7.73 C11.973,7.336 13.336,5.973 13.73,4.227 L14.268,1.835 C14.345,1.493 14.649,1.25 15,1.25 C15.351,1.25 15.655,1.493 15.732,1.835 Z M7.732,11.835 L8.116,13.543 C8.379,14.71 9.29,15.621 10.457,15.884 L12.165,16.268 C12.507,16.345 12.75,16.649 12.75,17 C12.75,17.351 12.507,17.655 12.165,17.732 L10.457,18.117 C9.29,18.379 8.379,19.29 8.116,20.456 L7.732,22.165 C7.655,22.507 7.351,22.75 7,22.75 C6.649,22.75 6.345,22.507 6.268,22.165 L5.884,20.456 C5.621,19.29 4.71,18.379 3.543,18.117 L1.835,17.732 C1.493,17.655 1.25,17.351 1.25,17 C1.25,16.649 1.493,16.345 1.835,16.268 L3.543,15.884 C4.71,15.621 5.621,14.71 5.884,13.543 L6.268,11.835 C6.345,11.493 6.649,11.25 7,11.25 C7.351,11.25 7.655,11.493 7.732,11.835 Z M11.231,9 C12.995,9.616 14.384,11.005 15,12.769 C15.616,11.005 17.005,9.616 18.769,9 C17.005,8.384 15.616,6.995 15,5.231 C14.384,6.995 12.995,8.384 11.231,9 Z M4.866,17 C5.797,17.451 6.549,18.203 7,19.134 C7.451,18.203 8.203,17.451 9.134,17 C8.203,16.549 7.451,15.797 7,14.866 C6.549,15.797 5.797,16.549 4.866,17 Z"
    />
  </svg>
);

const OpenIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="10" height="10" fill="none">
    <g transform="rotate(45 12 12)">
      <g transform="translate(0 -1)">
        <path
          fill="currentColor"
          stroke="currentColor"
          strokeWidth="0.3"
          d="M17.434,10.49 C15.2,7.965 12.66,5.751 11.999,5.751 C11.337,5.751 8.799,7.965 6.565,10.49 C6.289,10.8 5.817,10.828 5.506,10.555 C5.196,10.281 5.166,9.808 5.441,9.496 C5.486,9.444 6.581,8.211 7.889,6.955 C10.388,4.558 11.397,4.25 11.999,4.25 C12.6,4.25 13.608,4.557 16.108,6.955 C17.416,8.211 18.511,9.444 18.556,9.496 C18.83,9.806 18.801,10.28 18.492,10.555 C18.349,10.681 18.172,10.742 17.997,10.743 L17.995,10.743 C17.788,10.742 17.583,10.657 17.434,10.49 Z"
        />
      </g>
      <g transform="translate(0 1)">
        <path
          fill="currentColor"
          stroke="currentColor"
          strokeWidth="0.3"
          d="M7.892,17.036 C6.599,15.792 5.517,14.569 5.446,14.489 L5.444,14.487 C5.17,14.177 5.2,13.704 5.51,13.43 C5.821,13.156 6.294,13.186 6.569,13.496 C8.802,16.028 11.34,18.249 12.001,18.249 C12.663,18.249 15.203,16.027 17.436,13.496 C17.709,13.185 18.183,13.156 18.494,13.43 C18.804,13.704 18.834,14.177 18.56,14.487 L18.558,14.489 C18.488,14.569 17.405,15.792 16.112,17.036 C13.612,19.44 12.603,19.748 12.001,19.748 C11.401,19.748 10.391,19.44 7.892,17.036 Z"
        />
      </g>
    </g>
  </svg>
);

const PlayIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="10" height="10" fill="currentColor">
    <path d="M8 5.14v14.72a1 1 0 001.5.86l11-7.36a1 1 0 000-1.72l-11-7.36a1 1 0 00-1.5.86z" />
  </svg>
);

const ICON_PATTERN = /({{nextIcon}}|{{addIcon}}|{{createNotesIcon}}|{{openIcon}}|{{aiIslandIcon}}|{{gmailIcon}}|{{progressIcon}}|{{playIcon}})/;

const ICON_MAP: Record<string, { className: string; icon: ReactNode }> = {
  '{{nextIcon}}': { className: styles.inlineNextIcon, icon: <ChevronRight size={12} /> },
  '{{addIcon}}': { className: styles.inlineAddIcon, icon: <HugeiconsIcon icon={PlusSignIcon} size={10} strokeWidth={2.5} /> },
  '{{createNotesIcon}}': { className: styles.inlineCreateNotesIcon, icon: CreateNotesIcon },
  '{{openIcon}}': { className: styles.inlineOpenIcon, icon: OpenIcon },
  '{{aiIslandIcon}}': { className: styles.inlineAiIslandIcon, icon: <MailIcon /> },
  '{{gmailIcon}}': { className: styles.inlineGmailIcon, icon: <GmailIcon /> },
  '{{progressIcon}}': {
    className: styles.inlineProgressIcon,
    icon: <HugeiconsIcon icon={Analytics02Icon} size={12} strokeWidth={1.6} />,
  },
  '{{playIcon}}': { className: styles.inlinePlayIcon, icon: PlayIcon },
};

export const renderActionText = (actionText: string): ReactNode[] => {
  return actionText.split(ICON_PATTERN).map((part, i) => {
    const iconData = ICON_MAP[part];
    if (iconData) {
      return (
        <span key={i} className={iconData.className}>
          {iconData.icon}
        </span>
      );
    }
    return part;
  });
};

const DEFAULT_GAP = 12;
const VIEWPORT_PAD = 8;

function computePosition(
  rect: DOMRect,
  tw: number,
  th: number,
  placement: string,
  gap: number
): { top: number; left: number } {
  let top: number;
  let left: number;

  switch (placement) {
    case 'bottom':
    case 'bottom-start':
    case 'bottom-end':
      top = rect.bottom + gap;
      left = rect.left + rect.width / 2 - tw / 2;
      break;
    case 'top':
    case 'top-start':
    case 'top-end':
      top = rect.top - th - gap;
      left = rect.left + rect.width / 2 - tw / 2;
      break;
    case 'right':
      top = rect.top + rect.height / 2 - th / 2;
      left = rect.right + gap;
      break;
    case 'right-end':
      top = rect.bottom - th;
      left = rect.right + gap;
      break;
    case 'right-start':
      top = rect.top;
      left = rect.right + gap;
      break;
    case 'left':
      top = rect.top + rect.height / 2 - th / 2;
      left = rect.left - tw - gap;
      break;
    case 'left-end':
      top = rect.bottom - th;
      left = rect.left - tw - gap;
      break;
    case 'left-start':
      top = rect.top;
      left = rect.left - tw - gap;
      break;
    default:
      top = rect.bottom + gap;
      left = rect.left + rect.width / 2 - tw / 2;
  }

  top = Math.max(VIEWPORT_PAD, Math.min(top, window.innerHeight - th - VIEWPORT_PAD));
  left = Math.max(VIEWPORT_PAD, Math.min(left, window.innerWidth - tw - VIEWPORT_PAD));

  return { top, left };
}

interface CustomTooltipProps extends TooltipRenderProps {
  actionTextOverride?: string;
}

export const CustomTooltip = (props: CustomTooltipProps) => {
    const {
      closeProps,
      primaryProps,
      index,
      step,
      size,
      actionTextOverride,
    } = props;

    const stepData = step as unknown as PiqueOnboardingStep;
    const requiresInteraction = stepData.requiresInteraction ?? false;
    const actionText = stepData.actionText || actionTextOverride;
    const autoAdvance = stepData.autoAdvance;

    const { onClick: onCloseClick, 'aria-label': closeAriaLabel } = closeProps;
    const { onClick: onNextClick } = primaryProps;

    const placement = step.placement || 'bottom';
    const gap =
      (step.floaterProps as { offset?: number } | undefined)?.offset ??
      DEFAULT_GAP;
    const target = step.target as string;

    const tooltipRef = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

    useLayoutEffect(() => {
      let rafId: number;
      let lastTop = 0;
      let lastLeft = 0;

      const track = () => {
        const el = document.querySelector(target);
        const tooltip = tooltipRef.current;
        if (!el || !tooltip) {
          rafId = requestAnimationFrame(track);
          return;
        }

        const rect = el.getBoundingClientRect();
        const tw = tooltip.offsetWidth;
        const th = tooltip.offsetHeight;
        const { top, left } = computePosition(rect, tw, th, placement, gap);

        if (Math.abs(top - lastTop) > 0.5 || Math.abs(left - lastLeft) > 0.5) {
          lastTop = top;
          lastLeft = left;
          setPos({ top, left });
        }

        rafId = requestAnimationFrame(track);
      };

      rafId = requestAnimationFrame(track);
      return () => cancelAnimationFrame(rafId);
    }, [target, placement, gap]);

    return createPortal(
      <div
        ref={tooltipRef}
        className={styles.tooltipContainer}
        data-placement={placement}
        style={{
          position: 'fixed',
          top: pos?.top ?? -9999,
          left: pos?.left ?? -9999,
          zIndex: 10000,
        }}
        onClick={e => e.stopPropagation()}
      >
        <button
          className={styles.closeButton}
          onClick={onCloseClick}
          aria-label={closeAriaLabel}
        >
          <HugeiconsIcon icon={Cancel01Icon} size={12} strokeWidth={1} />
        </button>

        {step.title && (
          <div className={styles.tooltipHeader}>
            <h3 className={styles.tooltipTitle}>{step.title}</h3>
          </div>
        )}

        {step.content && (
          <div className={styles.tooltipContent}>{step.content}</div>
        )}

        {actionText && (
          <div className={styles.actionText}>{renderActionText(actionText)}</div>
        )}

        <div className={styles.tooltipFooter}>
          <div className={styles.stepIndicator}>
            {index + 1} of {size}
          </div>
          {!requiresInteraction && !autoAdvance && (
            <button className={styles.primaryButton} onClick={onNextClick}>
              <ChevronRight size={14} />
            </button>
          )}
        </div>

        {autoAdvance?.showProgressBar && (
          <div className={styles.progressBarContainer}>
            <div
              className={styles.progressBarFill}
              style={{ animationDuration: `${autoAdvance.duration}ms` }}
            />
          </div>
        )}
      </div>,
      document.body
    );
};
